import DeleteIcon from '@mui/icons-material/Delete'
import { Box, Chip, Grid, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import { GridRowSelectionModel } from '@mui/x-data-grid-premium'
import React from 'react'
import { Lead, User } from '../../types'
import MassDeleteDialog from '../DataGrid/MassDeleteDialog'

export interface MassUpdateLeadsProps {
  users: User[]
  selectedData: Lead | null
  selectedMassData: Lead[]
  setSelectedMassData: React.Dispatch<React.SetStateAction<Lead[]>>
  massUpdateData: any
  rowSelectionModel: GridRowSelectionModel
  rowCount: number
  onChange: (e: any) => void
  onUpdate: (massUpdateData: any, rowSelectionModel: GridRowSelectionModel) => void
  onClear: () => void
  onClickDelete: (event: React.MouseEvent<HTMLElement>, rowSelectionModel: GridRowSelectionModel) => void
  onClickMassDelete: (
    rowSelectionModel: GridRowSelectionModel,
    setSelectedMassData: React.Dispatch<React.SetStateAction<Lead[]>>
  ) => void
  onDelete: (setIsDeleting: React.Dispatch<React.SetStateAction<boolean>>) => void
  onMassDelete: (
    rowSelectionModel: GridRowSelectionModel,
    setRowSelectionModel: React.Dispatch<React.SetStateAction<GridRowSelectionModel>>
  ) => void
  isDeleting: boolean
  onClosePopover: () => void
  leads: Lead[] | []
  anchorEl: HTMLElement | null
  customPosition?: { top: number; left: number } | null
  onCloseMassDeletePopover: () => void
  isMassDeleteOpen: boolean
}

const statusOptions = [
  { value: 'Not Contacted', label: 'Not Contacted' },
  { value: 'Contacted', label: 'Contacted' },
]

const qualityOptions = [
  { value: 'Pending Review', label: 'Pending Review' },
  { value: 'Qualified', label: 'Qualified' },
  { value: 'Not Qualified', label: 'Not Qualified' },
]

const MassUpdateLeadsComponents: React.FC<MassUpdateLeadsProps> = ({
  users,
  selectedData,
  selectedMassData,
  setSelectedMassData,
  massUpdateData,
  rowSelectionModel,
  rowCount,
  onChange,
  onUpdate,
  onClear,
  onClickDelete,
  onClickMassDelete,
  onDelete,
  onMassDelete,
  isDeleting,
  onClosePopover,
  leads = [],
  anchorEl,
  customPosition,
  onCloseMassDeletePopover,
  isMassDeleteOpen,
}) => {
  const theme = useTheme()
  const taskGridStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    pb: 0.5,
    pl: 1.5,
    pr: 1.5,
    pt: 1,
    mt: 0.5,
  }

  const activeFields = Object.entries(massUpdateData || {}).filter(
    ([_, val]) =>
      val !== undefined &&
      val !== null &&
      (typeof val === 'string'
        ? val.trim().length > 0
        : Array.isArray(val)
        ? val.length > 0
        : typeof val === 'object' && Object.keys(val).length > 0)
  )

  const isFieldLocked = (fieldName: string) => {
    return activeFields.length > 0 && !activeFields.some(([key]) => key === fieldName)
  }

  const leadDesignation = Array.from(rowSelectionModel?.ids)?.length === 1 ? 'Lead Selected' : 'Leads Selected'

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        justifyContent: 'space-between',
        bgcolor: theme.palette.background.section,
      }}
    >
      {Array.from(rowSelectionModel?.ids).length > 0 ? (
        <Grid
          container
          sx={{
            maxHeight: 'auto',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            alignItems: 'center',
            zIndex: 1,
            flexWrap: 'wrap',
            overflow: 'hidden',
            p: 1,
          }}
        >
          <Grid container>
            <Grid item xs={2} sm={2} md={2} lg={2} xl={2} sx={taskGridStyles}>
              <Chip
                label={Array.from(rowSelectionModel?.ids).length}
                sx={{ color: 'rgb(24, 144, 255)', fontSize: 18 }}
              />
              <Typography>{leadDesignation}</Typography>
            </Grid>

            {/* Mass update fields temporarily disabled - using inline editing instead */}
            {/* <Grid item xs={1.7} sm={1.7} md={1.7} lg={1.7} xl={1.7} sx={taskGridStyles} justifyContent="flex-end">
              <MassUpdateTypeSelect
                name="status"
                value={massUpdateData.status || ''}
                options={statusOptions}
                displayLabel="Status"
                onChange={onChange}
                disabled={isFieldLocked('status')}
                sx={{ height: 40, minWidth: 150 }}
              />
            </Grid>

            <Grid item xs={1.7} sm={1.7} md={1.7} lg={1.7} xl={1.7} sx={taskGridStyles} justifyContent="flex-end">
              <MassUpdateTypeSelect
                name="quality"
                value={massUpdateData.quality || ''}
                options={qualityOptions}
                displayLabel="Quality"
                onChange={onChange}
                disabled={isFieldLocked('quality')}
                sx={{ height: 40, minWidth: 150 }}
              />
            </Grid>

            <Grid item xs={2.3} sm={2.3} md={2.3} lg={2.3} xl={2.3} sx={taskGridStyles} justifyContent="flex-end">
              <MassSingleUserSelect
                name="assignedUser"
                value={massUpdateData.assignedUser || null}
                options={users ?? []}
                displayLabel="Assigned To"
                disabled={isFieldLocked('assignedUser')}
                onChange={onChange}
              />
            </Grid> */}

            <Grid item xs sx={{ flexGrow: 1 }} />

            <Grid item sx={taskGridStyles} justifyContent="flex-end">
              {Array.from(rowSelectionModel?.ids)?.length === 1 && (
                <Tooltip title={'Delete Lead'} placement="top">
                  <IconButton color="inherit" onClick={e => onClickDelete(e, rowSelectionModel)}>
                    <DeleteIcon sx={{ fontSize: 30 }} />
                  </IconButton>
                </Tooltip>
              )}
              {Array.from(rowSelectionModel?.ids)?.length > 1 && (
                <Tooltip title={'Mass Delete'} placement="top">
                  <IconButton color="inherit" onClick={() => onClickMassDelete(rowSelectionModel, setSelectedMassData)}>
                    <DeleteIcon sx={{ fontSize: 30 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Grid
          container
          sx={{
            padding: 1,
            alignItems: 'center',
            minHeight: 50,
            gap: 1,
          }}
          justifyContent="flex-start"
        >
          <Grid item>
            <Typography variant="body2" mr={2}>
              Total Rows:
            </Typography>
          </Grid>
          <Grid item>
            <Typography sx={{ color: theme.palette.mode === 'dark' ? 'rgba(89, 224, 158,0.8)' : 'rgba(0,0,0,0.6)' }}>
              {rowCount}
            </Typography>
          </Grid>
          <Grid item>
            <Typography sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
              /
            </Typography>
          </Grid>
          <Grid item>
            <Typography sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
              {leads.length}
            </Typography>
          </Grid>
        </Grid>
      )}

      <MassDeleteDialog
        id={'mass-delete-popover'}
        open={isMassDeleteOpen}
        onClose={onCloseMassDeletePopover}
        handleMassDelete={onMassDelete}
        selectedMassData={selectedMassData}
        context={'leads'}
        customPosition={customPosition}
      />
    </Box>
  )
}

export default MassUpdateLeadsComponents
